<template>
    <div
        class="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8"
    >
        <div class="max-w-md w-full space-y-8">
            <!-- Logo and Title -->
            <div class="text-center">
                <div
                    class="mx-auto h-12 w-auto flex items-center justify-center"
                >
                    <div class="flex items-center space-x-2">
                        <div
                            class="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center"
                        >
                            <GraduationCap class="h-5 w-5 text-white" />
                        </div>
                        <span
                            class="text-2xl font-bold text-gray-900 dark:text-white"
                            >LMS</span
                        >
                    </div>
                </div>
                <h2
                    class="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white"
                >
                    Sign in to your account
                </h2>
                <!-- <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Or
                    <router-link
                        to="/register"
                        class="font-medium text-primary-600 hover:text-primary-500"
                    >
                        create a new account
                    </router-link>
                </p> -->
            </div>

            <!-- Login Form -->
            <form class="mt-8 space-y-6" @submit.prevent="handleLogin">
                <!-- Error Alert -->
                <div v-if="authStore.error" class="alert alert-danger">
                    {{ authStore.error }}
                </div>

                <!-- Email Field -->
                <div>
                    <label for="email" class="form-label">
                        Email address
                    </label>
                    <div class="mt-1">
                        <input
                            id="email"
                            v-model="form.email"
                            name="email"
                            type="email"
                            autocomplete="email"
                            required
                            :disabled="authStore.isLoading"
                            class="form-input"
                            :class="{
                                'border-danger-300 focus:border-danger-500':
                                    errors.email,
                            }"
                            placeholder="Enter your email"
                        />
                        <p v-if="errors.email" class="form-error">
                            {{ errors.email }}
                        </p>
                    </div>
                </div>

                <!-- Password Field -->
                <div>
                    <label for="password" class="form-label"> Password </label>
                    <div class="mt-1 relative">
                        <input
                            id="password"
                            v-model="form.password"
                            name="password"
                            :type="showPassword ? 'text' : 'password'"
                            autocomplete="current-password"
                            required
                            :disabled="authStore.isLoading"
                            class="form-input pr-10"
                            :class="{
                                'border-danger-300 focus:border-danger-500':
                                    errors.password,
                            }"
                            placeholder="Enter your password"
                        />
                        <button
                            type="button"
                            class="absolute inset-y-0 right-0 flex items-center pr-3"
                            @click="showPassword = !showPassword"
                        >
                            <EyeOff
                                v-if="showPassword"
                                class="h-5 w-5 text-gray-400 hover:text-gray-500"
                            />
                            <Eye
                                v-else
                                class="h-5 w-5 text-gray-400 hover:text-gray-500"
                            />
                        </button>
                        <p v-if="errors.password" class="form-error">
                            {{ errors.password }}
                        </p>
                    </div>
                </div>

                <!-- Remember Me & Forgot Password -->
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <input
                            id="remember"
                            v-model="form.remember"
                            name="remember"
                            type="checkbox"
                            class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-primary-600 dark:focus:ring-primary-500"
                        />
                        <label
                            for="remember"
                            class="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                        >
                            Remember me
                        </label>
                    </div>

                    <div class="text-sm">
                        <router-link
                            to="/forgot-password"
                            class="font-medium text-primary-600 hover:text-primary-500"
                        >
                            Forgot your password?
                        </router-link>
                    </div>
                </div>

                <!-- Submit Button -->
                <div>
                    <button
                        type="submit"
                        :disabled="authStore.isLoading"
                        class="btn btn-primary w-full flex justify-center"
                        :class="{
                            'opacity-75 cursor-not-allowed':
                                authStore.isLoading,
                        }"
                    >
                        <Loader2
                            v-if="authStore.isLoading"
                            class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        />
                        {{ authStore.isLoading ? "Signing in..." : "Sign in" }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { GraduationCap, Eye, EyeOff, Loader2 } from "@lucide/vue";
import type { LoginCredentials } from "@/types/auth";

const router = useRouter();
const authStore = useAuthStore();

// Form data
const form = reactive<LoginCredentials>({
    email: "",
    password: "",
    remember: false,
});

// UI state
const showPassword = ref(false);
const errors = reactive<Record<string, string>>({});

// Validate form
const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.email) {
        newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        newErrors.email = "Please enter a valid email address";
    }

    if (!form.password) {
        newErrors.password = "Password is required";
    } else if (form.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
    }

    // Clear old errors and set new ones
    Object.keys(errors).forEach((key) => delete errors[key]);
    Object.assign(errors, newErrors);

    return Object.keys(newErrors).length === 0;
};

// Handle login
const handleLogin = async () => {
    if (!validateForm()) {
        return;
    }

    const result = await authStore.login(form);

    if (result.success) {
        // Redirect to intended page or dashboard
        const redirect = router.currentRoute.value.query.redirect as string;
        router.push(redirect || "/dashboard");
    }
};

// Clear errors when form changes
const clearErrors = () => {
    Object.keys(errors).forEach((key) => delete errors[key]);
    authStore.setError(null);
};

// Watch form changes
watch([() => form.email, () => form.password], clearErrors);

// Check if already authenticated and redirect
onMounted(() => {
    if (authStore.isAuthenticated) {
        router.push("/dashboard");
    }
});
</script>
